import { useMemo, useState, type FormEvent } from 'react';
import { CreateQuotePartSchedule, type QuotePartSchedule, type Unit } from '@syndiqia/registry/contracts';
import { errorMessage } from '../api';
import { Alert, Disclosure, Empty, Field, Loading, PageHeader, validate, type FieldErrors } from '../components/ui';
import { useI18n } from '../i18n';
import { useBuildings, useCurrentQuoteParts, useQuotePartSchedules, useRegistryMutation, useUnits } from '../queries';
import { useSyndicate } from '../session';

export function QuotePartsPage() {
  const { t, number, date } = useI18n();
  const syndicate = useSyndicate();
  const current = useCurrentQuoteParts();
  const history = useQuotePartSchedules();
  const units = useUnits();
  const label = useUnitLabel();

  return (
    <>
      <PageHeader
        title={t.quoteParts.title}
        intro={t.quoteParts.intro}
        actions={
          syndicate.can('registry.quote_parts.write') &&
          units.data && (
            <Disclosure label={t.quoteParts.new}>
              {(close) =>
                units.data.length ? (
                  <ScheduleForm units={units.data} current={current.data ?? null} onDone={close} />
                ) : (
                  <Alert tone="info">{t.quoteParts.needUnits}</Alert>
                )
              }
            </Disclosure>
          )
        }
      />

      <h2>{t.quoteParts.current}</h2>
      {current.isPending && <Loading label={t.loading} />}
      {current.isError && <Alert>{errorMessage(current.error, t)}</Alert>}
      {current.data === null && <Empty>{t.quoteParts.none}</Empty>}
      {current.data && (
        <>
          <p className="muted">
            {t.quoteParts.from} {date(current.data.from)} · {t.quoteParts.source} : {current.data.source}
          </p>
          <table className="table">
            <thead>
              <tr>
                <th>{t.quoteParts.unit}</th>
                <th className="num">{t.quoteParts.value}</th>
                <th className="num">{t.quoteParts.percent}</th>
              </tr>
            </thead>
            <tbody>
              {current.data.parts.map((p) => (
                <tr key={p.unitId}>
                  <td>{label(p.unitId)}</td>
                  <td className="num">
                    {number(p.numerator)} / {number(current.data!.denominator)}
                  </td>
                  <td className="num">{number((p.numerator / current.data!.denominator) * 100, 3)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th>{t.quoteParts.total}</th>
                <th className="num">{number(current.data.parts.reduce((s, p) => s + p.numerator, 0))}</th>
                <th className="num">{number(100, 3)}</th>
              </tr>
            </tfoot>
          </table>
        </>
      )}

      {!!history.data?.length && (
        <>
          <h2>{t.quoteParts.history}</h2>
          <ul className="list">
            {history.data.map((s) => (
              <li key={s.id}>
                <span className={`badge badge-${s.status}`}>{t.quoteParts.status[s.status]}</span> {t.quoteParts.from}{' '}
                {date(s.from)}
                {s.to && ` ${t.quoteParts.to} ${date(s.to)}`} · {s.source}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

function useUnitLabel() {
  const units = useUnits();
  const buildings = useBuildings();
  return (unitId: string) => {
    const unit = units.data?.find((u) => u.id === unitId);
    if (!unit) return '…';
    const multipleBuildings = (buildings.data?.length ?? 0) > 1;
    const building = buildings.data?.find((b) => b.id === unit.buildingId);
    return multipleBuildings && building ? `${building.name} — ${unit.label}` : unit.label;
  };
}

function ScheduleForm({ units, current, onDone }: { units: Unit[]; current: QuotePartSchedule | null; onDone: () => void }) {
  const { t, number } = useI18n();
  const label = useUnitLabel();
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [source, setSource] = useState('');
  const [denominator, setDenominator] = useState(String(current?.denominator ?? 10000));
  // Start from the schedule in force, so an amendment only changes what differs.
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(units.map((u) => [u.id, String(current?.parts.find((p) => p.unitId === u.id)?.numerator ?? '')])),
  );
  const [errors, setErrors] = useState<FieldErrors>({});

  const total = useMemo(() => Object.values(values).reduce((s, v) => s + (Number(v) || 0), 0), [values]);
  const remaining = (Number(denominator) || 0) - total;

  const save = useRegistryMutation<CreateQuotePartSchedule, QuotePartSchedule>(
    async (api, base, input) => {
      const draft = await api<QuotePartSchedule>(`${base}/quote-part-schedules`, { method: 'POST', body: input });
      return api<QuotePartSchedule>(`${base}/quote-part-schedules/${draft.id}/activate`, { method: 'POST' });
    },
    ['quote-parts'],
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parts = units.filter((u) => values[u.id]?.trim()).map((u) => ({ unitId: u.id, numerator: Number(values[u.id]) }));
    const result = validate(CreateQuotePartSchedule, { effectiveFrom, source, denominator: Number(denominator), parts });
    if (result.errors) return setErrors(result.errors);
    if (remaining !== 0) return setErrors({ parts: t.quoteParts.sumMismatch });
    setErrors({});
    save.mutate(result.data, { onSuccess: onDone });
  };

  return (
    <form onSubmit={submit} className="form" noValidate>
      <div className="row">
        <Field label={t.quoteParts.effectiveFrom} error={errors.effectiveFrom}>
          <input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
        </Field>
        <Field label={t.quoteParts.denominator} error={errors.denominator}>
          <input value={denominator} onChange={(e) => setDenominator(e.target.value)} inputMode="numeric" />
        </Field>
      </div>
      <Field label={t.quoteParts.source} error={errors.source}>
        <input value={source} onChange={(e) => setSource(e.target.value)} placeholder={t.quoteParts.sourcePlaceholder} />
      </Field>
      <table className="table table-compact">
        <thead>
          <tr>
            <th>{t.quoteParts.unit}</th>
            <th className="num">{t.quoteParts.value}</th>
          </tr>
        </thead>
        <tbody>
          {units.map((u) => (
            <tr key={u.id}>
              <td>{label(u.id)}</td>
              <td className="num">
                <input
                  className="input-num"
                  value={values[u.id] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [u.id]: e.target.value }))}
                  inputMode="numeric"
                  aria-label={`${t.quoteParts.value} ${u.label}`}
                />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className={remaining === 0 ? 'ok' : 'warn'}>
            <th>{t.quoteParts.remaining}</th>
            <th className="num">{number(remaining)}</th>
          </tr>
        </tfoot>
      </table>
      {errors.parts && <Alert>{errors.parts}</Alert>}
      {save.isError && <Alert>{errorMessage(save.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={save.isPending || remaining !== 0}>
          {t.quoteParts.activate}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}
